<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import { useRouter, useRoute } from 'vue-router';

import type { ConsultationDto, FormDto, Locales, SectionDto } from '@/services/types/api.types';
import { useFormsStore, useSectionsStore } from '@/stores';
import { getTranslation } from '@/composables/data-display-utils';
import { canBeMandatory, formTitle, invalidFormIds as findInvalidFormIds } from '@/helpers/visibilityRules.helper';
import ConditionEditorDialog from '@/components/dialogs/ConditionEditorDialog.vue';

const { t } = useI18n();
const $q = useQuasar();
const router = useRouter();
const route = useRoute();
const formsStore = useFormsStore();
const sectionsStore = useSectionsStore();

const props = defineProps<{
  consultation: ConsultationDto;
  languageEnabled: Locales[];
}>();

type FilterKey = 'all' | 'configured' | 'mandatory' | 'conditional' | 'invalid';

const allForms = ref<FormDto[]>([]);
const sortedSections = ref<SectionDto[]>([]);
const isLoading = ref(false);
const isLoaded = ref(false);
const activeFilter = ref<FilterKey>('all');
const searchQuery = ref('');

async function loadForms() {
  if (isLoading.value || isLoaded.value) return;
  isLoading.value = true;
  try {
    await sectionsStore.fetchSectionsByConsultation(props.consultation.id!);
    const sections = sectionsStore.getSectionsByConsultationId(props.consultation.id!);
    sortedSections.value = [...sections].sort((a, b) => a.order_num - b.order_num);
    for (const s of sortedSections.value) {
      if (s.id) await formsStore.fetchFormsBySection(s.id);
    }
    const forms: FormDto[] = [];
    for (const s of sortedSections.value) {
      if (!s.id) continue;
      const sectionForms = formsStore.getFormsBySectionId(s.id);
      forms.push(...[...sectionForms].sort((a, b) => a.order_num - b.order_num));
    }
    allForms.value = forms;
  } finally {
    isLoading.value = false;
    isLoaded.value = true;
  }
}

watch(
  () => props.consultation.id,
  () => {
    isLoaded.value = false;
  },
  { immediate: false },
);

const ruleSets = computed(() => props.consultation.options?.guided_consultation?.visibility?.ruleSets ?? []);

const mandatoryFormIds = computed(() => new Set(allForms.value.filter((f) => f.is_mandatory === 1).map((f) => f.id)));

const conditionalFormIds = computed(() => new Set(ruleSets.value.map((rs) => rs.targetId)));

const invalidFormIds = computed(() => findInvalidFormIds(ruleSets.value, allForms.value));

const configuredFormIds = computed(
  () => new Set([...mandatoryFormIds.value, ...conditionalFormIds.value, ...invalidFormIds.value]),
);

const counters = computed(() => ({
  configured: configuredFormIds.value.size,
  all: allForms.value.length,
  mandatory: mandatoryFormIds.value.size,
  conditional: conditionalFormIds.value.size,
  invalid: invalidFormIds.value.size,
}));

function formMatchesFilter(form: FormDto): boolean {
  switch (activeFilter.value) {
    case 'configured':
      return configuredFormIds.value.has(form.id);
    case 'mandatory':
      return mandatoryFormIds.value.has(form.id);
    case 'conditional':
      return conditionalFormIds.value.has(form.id);
    case 'invalid':
      return invalidFormIds.value.has(form.id);
    default:
      return true;
  }
}

function formMatchesSearch(form: FormDto): boolean {
  if (!searchQuery.value.trim()) return true;
  const q = searchQuery.value.toLowerCase();
  const title = getTranslation(form.question, 'title', props.languageEnabled).toLowerCase();
  return title.includes(q);
}

interface SectionGroup {
  section: SectionDto;
  forms: FormDto[];
}

const sectionGroups = computed<SectionGroup[]>(() => {
  return sortedSections.value
    .map((section) => {
      if (!section.id) return null;
      const sectionForms = allForms.value.filter(
        (f) => f.section_id === section.id && formMatchesFilter(f) && formMatchesSearch(f),
      );
      return { section, forms: sectionForms };
    })
    .filter((g): g is SectionGroup => g !== null && g.forms.length > 0);
});

function getFormTitle(form: FormDto): string {
  return formTitle(allForms.value, form.id, props.languageEnabled);
}

function getConditionLabel(formId: number): string {
  const ruleSet = ruleSets.value.find((rs) => rs.targetId === formId);
  if (!ruleSet || ruleSet.conditions.length === 0) return '';
  const cond = ruleSet.conditions[0];
  const sourceForm = allForms.value.find((f) => f.id === cond.sourceFormId);
  const sourceName = formTitle(allForms.value, cond.sourceFormId, props.languageEnabled);
  const choiceId = cond.valueIds?.[0];
  let answerText = String(choiceId ?? '');
  if (sourceForm && choiceId != null) {
    const choice = sourceForm.answers?.steps?.[0]?.choices?.find((c: any) => c.id === choiceId);
    if (choice) answerText = getTranslation(choice, 'text', props.languageEnabled) || answerText;
  }
  return t('form.guided-consultation.dashboard.conditionalDetail', { source: sourceName, answer: answerText });
}

function navigateToForm(formId: number) {
  router.push({ name: 'form_details', params: { id: formId } });
}

async function toggleMandatory(form: FormDto) {
  const idx = allForms.value.findIndex((f) => f.id === form.id);
  if (idx === -1) return;
  const newMandatory = form.is_mandatory === 1 ? 0 : 1;
  allForms.value[idx] = { ...allForms.value[idx], is_mandatory: newMandatory };
  await formsStore.saveForm({ ...form, is_mandatory: newMandatory });
}

function openConditionEditor(formId: number) {
  $q.dialog({
    component: ConditionEditorDialog,
    componentProps: {
      formId,
      consultation: props.consultation,
      languageEnabled: props.languageEnabled,
    },
  });
}

// Handle ?editForm= query param coming from the form detail page
const pendingEditFormId = ref<number | null>(null);
const editFormQuery = Number(route.query.editForm);
if (editFormQuery) {
  pendingEditFormId.value = editFormQuery;
}

watch(isLoaded, (loaded) => {
  if (loaded && pendingEditFormId.value) {
    openConditionEditor(pendingEditFormId.value);
    pendingEditFormId.value = null;
  }
});

loadForms();
</script>

<template>
  <q-card class="q-mb-md">
    <q-card-section>
      <q-item-label header class="text-h6">{{ t('form.guided-consultation.dashboard.title') }}</q-item-label>
    </q-card-section>
    <q-separator />
    <q-card-section>
      <div v-if="isLoading" class="q-pa-sm text-center">
        <q-spinner-dots color="primary" size="md" />
      </div>
      <template v-else-if="isLoaded">
        <div class="row q-gutter-xs q-mb-md items-center">
          <q-chip
            v-for="filter in ['all', 'configured', 'mandatory', 'conditional', 'invalid'] as FilterKey[]"
            :key="filter"
            :color="activeFilter === filter ? (filter === 'invalid' ? 'negative' : 'primary') : 'grey-3'"
            :text-color="activeFilter === filter ? 'white' : 'grey-8'"
            :icon="filter === 'invalid' ? 'warning' : undefined"
            clickable
            @click="activeFilter = filter"
            class="no-border-radius"
          >
            {{ t(`form.guided-consultation.dashboard.filters.${filter}`) }}
            <span class="q-ml-xs text-caption">({{ counters[filter] }})</span>
          </q-chip>
          <q-space />
          <q-input
            v-model="searchQuery"
            :placeholder="t('form.guided-consultation.dashboard.searchPlaceholder')"
            dense
            outlined
            clearable
            style="min-width: 200px"
          >
            <template #prepend>
              <q-icon name="search" />
            </template>
          </q-input>
        </div>

        <div v-if="sectionGroups.length === 0" class="text-caption text-grey-7 q-pa-sm">
          {{ t('form.guided-consultation.dashboard.noResults') }}
        </div>

        <q-expansion-item
          v-for="group in sectionGroups"
          :key="group.section.id"
          :label="getTranslation(group.section, 'title', languageEnabled)"
          :caption="t('form.guided-consultation.dashboard.questionsCount', { count: group.forms.length })"
          default-opened
          header-class="text-weight-bold bg-grey-2"
          class="q-mb-sm rounded-borders overflow-hidden"
          style="border: 1px solid rgba(0, 0, 0, 0.12)"
        >
          <q-list separator>
            <q-item
              v-for="form in group.forms"
              :key="form.id"
              clickable
              v-ripple
              @click="navigateToForm(form.id)"
              class="q-py-sm"
            >
              <q-item-section>
                <q-item-label class="text-body">{{ getFormTitle(form) }}</q-item-label>
                <q-item-label
                  v-if="conditionalFormIds.has(form.id) && !invalidFormIds.has(form.id)"
                  caption
                  class="text-grey-7 q-mt-xs"
                >
                  {{ getConditionLabel(form.id) }}
                </q-item-label>
              </q-item-section>
              <q-item-section side>
                <div class="row items-center q-gutter-sm no-wrap">
                  <!-- A mandatory question of a type that cannot be one stays clickable, so it can be turned off -->
                  <div>
                    <q-btn
                      :outline="!mandatoryFormIds.has(form.id)"
                      :unelevated="mandatoryFormIds.has(form.id)"
                      :color="mandatoryFormIds.has(form.id) ? 'blue' : 'grey-7'"
                      :disable="!canBeMandatory(form) && !mandatoryFormIds.has(form.id)"
                      icon="lock"
                      size="md"
                      no-caps
                      :label="t('form.guided-consultation.summary.mandatory')"
                      @click.stop="toggleMandatory(form)"
                    />
                    <q-tooltip v-if="!canBeMandatory(form)">
                      {{ t('form.guided-consultation.mandatory.unsupportedType') }}
                    </q-tooltip>
                  </div>
                  <q-btn
                    :outline="!conditionalFormIds.has(form.id)"
                    :unelevated="conditionalFormIds.has(form.id)"
                    :color="
                      invalidFormIds.has(form.id) ? 'negative' : conditionalFormIds.has(form.id) ? 'purple' : 'grey-7'
                    "
                    icon="o_visibility"
                    size="md"
                    no-caps
                    :label="t('form.guided-consultation.summary.conditional')"
                    @click.stop="openConditionEditor(form.id)"
                  />
                </div>
              </q-item-section>
            </q-item>
          </q-list>
        </q-expansion-item>
      </template>
    </q-card-section>
  </q-card>
</template>
